import { HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { PointsRemainingChip } from '@/components/shared/PointsRemainingChip';
import { Routes } from '@/constants/Routes';
import { BookingStatusBadge } from '@/pages/CustomerDashboard/Bookings/BookingStatusBadge';
import { useDashboardNavigate } from '@/pages/CustomerDashboard/helpers';
import { ApiResolvedBooking } from '@api/interfaces/ApiOrderTransaction';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { ButtonBase, Stack, useTheme } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

interface BookingListItemProps {
  accountName?: string,
  booking: ApiResolvedBooking,
  showAccountName?: boolean,
}

export const BookingListItem = ({
  accountName,
  booking,
  showAccountName = false,
}: BookingListItemProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();

  const reservedPointsTag = useMemo(() => {
    if (booking.pointsRemaining === 0 && booking.pointsRedeemed === 0) {
      return null;
    }

    const isFullyRedeemed = booking.pointsRemaining === 0 && booking.pointsRedeemed > 0;

    if (isFullyRedeemed) {
      return (
        <PointsRemainingChip
          bgColor="#C1FFFF"
          text={t(
            $ => $.bookings.reservedPoints.allRedeemedWithEmoji,
            { points: booking.pointsRedeemed },
          )}
        />
      );
    }

    if (booking.pointsRemaining > 0) {
      return (
        <PointsRemainingChip
          text={t(
            $ => $.common.booking.pointsRemaining,
            { count: booking.pointsRemaining },
          )}
        />
      );
    }

    return null;
  }, [booking, t]);

  const bookingErpId = booking.groupingId;

  return (
    <ButtonBase
      onClick={() => navigateToPage(
        Routes.CUSTOMER_DASHBOARD_BOOKING_DETAIL,
        accessToken,
        { bookingId: booking.id },
      )}
      sx={{
        borderRadius: '8px',
        width: '100%',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        p="16px"
        sx={{
          bgcolor: 'white',
          width: '100%',
          display: 'flex',
        }}
      >
        <Stack flex={1} minWidth={0}>
          {showAccountName && accountName && (
            <>
              <Text
                category="body-small"
                sx={{
                  color: theme.palette.grey[600],
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  display:'flex',
                  textAlign: 'left',
                }}
              >
                {accountName}
              </Text>
              <VSpacer size="2" />
            </>
          )}
          <Text
            breakWord
            category="title-medium"
            textAlign="left"
          >
            {t($ => $.orders.details.booking, { bookingId: bookingErpId })}
          </Text>
          {reservedPointsTag && (
            <>
              <VSpacer size="4" />
              {reservedPointsTag}
            </>
          )}
        </Stack>
        <HSpacer size="3" />
        <Stack
          alignItems="center"
          direction="row"
          gap="8px"
          sx={{ flexShrink: 0 }}
        >
          <BookingStatusBadge
            bookingData={{
              isClosed: booking.isClosed ?? false,
              expirationDate: booking.expirationDate ? new Date(booking.expirationDate) : null,
            }}
          />
          <ChevronRight sx={{ color: theme.palette.text.secondary }} />
        </Stack>
      </Stack>
    </ButtonBase>
  );
};
